import React, { useEffect, useRef, JSX } from 'react';
import {
  SearchParams,
  useParameterManager,
  serialize,
  deserialize,
  useContext,
} from '@greystar/lib';

export const ParameterManagerCustom = ({
  pathName,
  searchParams,
}: {
  pathName: string | null;
  searchParams: SearchParams;
}): JSX.Element => {
  const { state, methods } = useParameterManager();
  const { state: contextState } = useContext();

  const previousUrl = useRef(pathName);

  // Keep a reference to the last non-empty parameters
  const lastValidParameters = useRef(state.parameters);
  const isEmpty = (obj: object) => Object.keys(obj).length === 0;

  /**
   * Coveo by design clears filters (stored in state.parameters) when the view is changed
   * The view will change when users scroll the map
   * For a good user experience, we don't want to clear the filters unless the user explicitly does so
   * So we need to detect when the user explicitly clears the filters (state.parameters = { page: undefined })
   * Distinct from when Coveo clears the filters due to a view change (state.paramters = {})
   */

  /**
   * When the URL search parameters change, this effect deserializes them and synchronizes them into the
   * ParameterManager controller's state.
   */
  useEffect(() => {
    if (methods === undefined) {
      return;
    }

    const newCommerceParams = deserialize(searchParams);

    const newUrl = serialize(newCommerceParams, searchParams.requestPath);

    if (newUrl === previousUrl.current) {
      return;
    }

    previousUrl.current = newUrl;
    methods.synchronize(newCommerceParams);
  }, [methods, searchParams]);

  /**
   * When the ParameterManager controller's state changes, this effect serializes it into the URL and pushes the new URL
   * to the browser history.
   * */
  useEffect(() => {
    // Ensures that the effect only executes if the controller is hydrated, so that it plays well with the other effect.
    if (methods === undefined || typeof window === 'undefined') {
      return;
    }

    // Only update lastValidParameters if state.parameters is not empty i.e. a user selected or cleared parameter state
    if (!isEmpty(state.parameters)) {
      lastValidParameters.current = state.parameters;
    }

    // Use lastValidParameters.current if state.parameters is empty
    const paramsToUse = isEmpty(state.parameters)
      ? lastValidParameters.current // Continue to use the last valid parameters selected by the user because Coveo has result due to a view change
      : state.parameters; // Use the new parameters coming from Coveo

    const coveoViewUrlObj = new URL(contextState.view.url);
    const coveoViewUrlPath = coveoViewUrlObj.pathname.split('/').splice(2);

    const newUrlStr = serialize(paramsToUse, coveoViewUrlPath);

    // Compare pathnames (ignore query string when detecting view changes)
    const pathChanged = previousUrl.current !== newUrlStr || isEmpty(state.parameters);

    if (!pathChanged) {
      return;
    }

    //const mergedUrl = newUrlObj.toString();
    const mergedUrl = `${newUrlStr}${window.location.search}`;

    previousUrl.current = mergedUrl;
    history.pushState(null, document.title, mergedUrl);
  }, [methods, state.parameters, searchParams.requestPath]);

  return <></>;
};
